import axios from 'axios';
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addNotification, setNotifications } from './notificationsSlice';

const functionWrapper = (connection, methodName, ...args) => {
	const func = retryFunc =>
		connection.invoke(methodName, ...args).catch(error => {
			const isDisconnected =
				connection.state === HubConnectionState.Disconnecting ||
				connection.state === HubConnectionState.Disconnected;

			if (isDisconnected && !!retryFunc) {
				setTimeout(
					() =>
						connection
							.start()
							.then(() => retryFunc())
							.catch(() => retryFunc(retryFunc)),
					retryFunc ? 30000 : 0
				);
				return;
			}

			console.error(error);
		});

	return func(func);
};

export const initConnection = createAsyncThunk(
	'notifications/hubConnection/initConnection',
	async (_params, { dispatch }) => {
		const connection = new HubConnectionBuilder()
			.withUrl(`${axios.defaults.baseURL}/hubs/notifications`, {
				accessTokenFactory: () => window.localStorage.getItem('accessToken') || ''
			})
			.withAutomaticReconnect([5_000, 10_000, 30_000, 60_000, 90_000])
			.configureLogging(LogLevel.Error)
			.build();

		connection.on('newConnection', connInfo => {
			if (import.meta.env.DEV) {
				console.log('[Notification Hub] New connection', connInfo);
			}
		});

		connection.on('fetchNotifications', notifications => {
			dispatch(setNotifications(notifications || []));
		});

		connection.on('newNotification', notification => {
			dispatch(addNotification(notification));
		});

		return connection.start().then(() => functionWrapper(connection, 'addNewConnection').then(() => connection));
	}
);

export const sendNotification = createAsyncThunk<void, { message: any }, { state: any }>(
	'notifications/hubConnection/sendNotification',
	async ({ message }, { getState }) => {
		const { hubConnection } = getState().notifications;
		return functionWrapper(hubConnection, 'send', message);
	}
);

export const readNotification = createAsyncThunk<void, { notificationUid: string }, { state: any }>(
	'notifications/hubConnection/readNotification',
	async ({ notificationUid }, { getState }) => {
		const { hubConnection } = getState().notifications;
		return functionWrapper(hubConnection, 'read', notificationUid);
	}
);

export const dismissNotification = createAsyncThunk<void, { notificationUid: string }, { state: any }>(
	'notifications/hubConnection/dismissNotification',
	async ({ notificationUid }, { getState }) => {
		const { hubConnection } = getState().notifications;
		return functionWrapper(hubConnection, 'dismiss', [notificationUid]);
	}
);

export const dismissAllNotifications = createAsyncThunk<void, void, { state: any }>(
	'notifications/hubConnection/dismissAllNotifications',
	async (params, { getState }) => {
		const { hubConnection, list } = getState().notifications;
		return functionWrapper(hubConnection, 'dismiss', list.ids || []);
	}
);

const initialState: HubConnection | null = null;

const notificationsHubSlice = createSlice({
	name: 'notifications/hubConnection',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(initConnection.fulfilled, (state, action) => action.payload);
	}
});

export default notificationsHubSlice.reducer;
